// Various Choice API calls.
//  FUTURE: Move to Swagger client library generator
import { PrivilegeType, IntakeFilterOption, DatabaseIdTypes } from '../Enums/ChoiceEnums';
import { IPayerFinancialClassPickerInfoModel, IPayerPickerInfoModel, IProcedureCodePickerInfoModel, IRenderingProviderPickerInfoModel, IServiceLocationInfoModel } from "./Models/OptionValuesModels";
import { ISearchBaseInputs, ISearchResourceInputs, ISearchResourceResults, ISearchPayerInputs, ISearchPayerResults,
         ISearchLoincInputs, ISearchLoincItem,
         ISearchPaymentBatchInputs,ISearchPaymentBatchResults,
         ISearchPaymentPlanInputs,ISearchPaymentPlanResults } from "./Models/SearchModel";
import { ICCDASummaryInputs, ICCDASummaryResponse } from "./Models/CCDAModel";
import { IUserNoteSaveNoteInputs, IUserNoteSaveNoteResult, IUserNoteTableResults } from "./Models/UserNoteModel";
import { IEntityInfoById } from './Models/EntityModel';
import { IDatabaseIdModel } from './Models/DatabaseIdModel';
import { FhirApiModel } from "./Models/FHIR/FhirApiModel";
import { ClinicianModel } from "./Models/FHIR/ClinicianModel";
import { IDocumentTypeSave, IDocumentTypeSaveResponse } from "./Models/Document/DocumentTypeModel";
import { NoTzDate } from "./DateUtils";
import { DisplayStyle } from '../Enums/UiEnums';
import { showToast } from './Notification';


export type ErrorCallbackFunction = (httpResp: Response, errorMessage?: string) => void;

// Override the base Uri
export function setBaseUri(overridingBaseUri: string): void {
    baseUri = overridingBaseUri;
}

export function getBaseUri(): string {
    return baseUri;
}

export function getOfficeMdBaseUri(): string {
    if ("" === location.port) {
        return `//${location.host}/officemd/`;
    }

    return `//${location.host}:${location.port}/officemd/`;
}

// Default to OfficeMd as the base URI
let baseUri: string = getOfficeMdBaseUri();

// Generic API response interface
export interface ApiResponseSimple<TData>
{
    success:boolean,
    data?: TData,
    message?: string
}
 
export const resource = {
    async search(payload: ISearchResourceInputs): Promise<ISearchResourceResults> {
        return (await postJson("api/Resource/Search", payload)) as ISearchResourceResults;
    }
};

export const payer = {
    async search(payload: ISearchPayerInputs): Promise<ISearchPayerResults> {
        return (await postJson("api/Payer/Search", payload)) as ISearchPayerResults;
    }
};

export const paymentbatch = {
    async search(payload: ISearchPaymentBatchInputs): Promise<ISearchPaymentBatchResults> {
        return (await postJson("api/Billing/PaymentBatch/Search", payload)) as ISearchPaymentBatchResults;
    }
};
export const paymentplan= {
    async search(payload: ISearchPaymentPlanInputs): Promise<ISearchPaymentPlanResults> {
        return (await postJson("api/Billing/PaymentPlan/Search", payload)) as ISearchPaymentPlanResults;
    }
};
export const apptTypeProcedure = {
    async search(payload: IApptTypeSearchInputs): Promise<Array<IApptTypeProcedureSearchItem>> {
        return (await postJson("api/Patient/CostEstimator/GetApptTypesWithProcedures", payload)) as Array<IApptTypeProcedureSearchItem>;
    }
};

export const provider = {
    async search(payload: IProviderSearchInputs): Promise<Array<IProviderSearchItem>> {
        return (await postJson("api/Provider/Search", payload)) as Array<IProviderSearchItem>;
    }
};

export const codeSearchProcedure = {
    async search(payload: ICodeSearchInputs): Promise<Array<ICodeSearchItem>> {
        return (await postJson("api/Setup/Procedure/Search", payload)) as Array<ICodeSearchItem>;
    }
};

export const codeSearchDiagnosis = {
    async search(payload: ISearchBaseInputs): Promise<Array<ICodeSearchItem>> {
        return (await postJson("api/Setup/Diagnosis/Search", payload)) as Array<ICodeSearchItem>;
    }
};

export const loinc = {
    async search(payload: ISearchLoincInputs): Promise<Array<ISearchLoincItem>> {
        return (await getJson(encodeURI(`api/Loinc?searchText=${payload.searchText ?? ""}&loincCode=${payload.loincCode ?? ""}&displayAllResults=${payload.displayAllResults}`))) as Array<ISearchLoincItem>;
    }
};

export const documentType = {
    async save(payload: IDocumentTypeSave): Promise<IDocumentTypeSaveResponse> {
        return (await postJson("api/Setup/DocumentType/Save", payload)) as IDocumentTypeSaveResponse;
    }
};

export const order = {
    async patient(orderId: number) {
        return getJson(`api/Order/${orderId}/Patient`);
    }
}

export const userNote = {
    async SaveNote(payload: IUserNoteSaveNoteInputs): Promise<IUserNoteSaveNoteResult> {
        return (await postJson("api/UserNote/SaveNote", payload)) as IUserNoteSaveNoteResult;   
    },
    async GetNoteList(noteType: DatabaseIdTypes, patientId: number, noteIdentifier: number): Promise<IUserNoteTableResults> {
        return (await getJson(`api/UserNote/NoteList/${noteType}/${patientId}/${noteIdentifier}`)) as IUserNoteTableResults;
    }
}

export const ccdaBuilder = {
    async Summary(payload: ICCDASummaryInputs): Promise<ICCDASummaryResponse> {
        return (await postJson("api/CCDA/Summary", payload)) as ICCDASummaryResponse;
    }
}

// Methods used to get option values used for filters or selection
export const optionValues = {
    async PayerFinancialClassPickerInfo(includeUnspecified: boolean = false): Promise<IPayerFinancialClassPickerInfoModel[]> {
        let result = (await getJson("api/OptionValues/PayerFinancialClassPickerInfo")) as IPayerFinancialClassPickerInfoModel[];

        // Should we add the 'Unspecified' financial class?
        if (includeUnspecified) {
            result.push({ financial_class_id: 0, name: "Unspecified" });
        }
        return result;
    },

    async RenderingProviderPickerInfo(): Promise<IRenderingProviderPickerInfoModel[]> {
        return (await getJson("api/OptionValues/RenderingProviderPickerInfo")) as IRenderingProviderPickerInfoModel[];
    },

    async PayerPickerInfo(): Promise<IPayerPickerInfoModel[]> {
        return (await getJson("api/OptionValues/PayerPickerInfo")) as IPayerPickerInfoModel[];
    },

    async ServiceLocationPickerInfo(): Promise<IServiceLocationInfoModel[]> {
        return (await getJson("api/OptionValues/ServiceLocationPickerInfo")) as IServiceLocationInfoModel[];
    },

    async ProcedureCodePickerInfo(): Promise<IProcedureCodePickerInfoModel[]> {
        const result = await getJson("api/OptionValues/ProcedureCodePickerInfo");
        return result as IProcedureCodePickerInfoModel[];
    }
}


export const emrTemplate = {
    async GetIntakeFilter(templateId: number): Promise<IIntakeFilterModel> {
        return (await getJson(`api/emrtemplate/${templateId}`)) as IIntakeFilterModel
    },

    // Save the default filter
    async saveIntakeFilter(payload: IIntakeFilterModel) {
        await postVoid("api/emrtemplate/SaveIntakeFilter", payload)
    }
}

export const entityInfo = {
    async getEntityInfoById(entityId: number): Promise<IEntityInfoById> {
        const result = await getJson(`api/Entity/GetEntityInfoById/${entityId}`);
        return result as IEntityInfoById;
    },

    async getEntityInfoByDatabaseId(databaseId: IDatabaseIdModel): Promise<IEntityInfoById> {
        const result = await getJson(`api/Entity/Info/${databaseId.idType}/${databaseId.id}`);
        return result as IEntityInfoById;
    }
}

export const billingActions = {
    async options() {
        return (await getJson(`api/Billing/Actions/Options`)) as IBillingActionOption[];
    }
}

export interface IBillingActionOption {
    biller_action_id: number,
    description: string,
    action_days: number,
    active: boolean
}


export interface IUserConfigurationScreens {
    screenId: string;
    screenName: string;
    height: number;
    width: number;
    security: PrivilegeType;
}

export interface IApptTypeSearchKey {
    display?: string
    searchText?: string,
}
export interface IApptTypeSearchInputs {
    searchText?: string,
    displayAllResults: boolean
}
export interface IApptTypeSearchResults {
    searchList?: Array<IApptTypeProcedureSearchItem>
}

export interface IApptTypeProcedureSearchItem {
    appointmentTypeId?: number,
    appointmentTypeName?: string,
    appointmentTypeDuration?: number
}

export interface IProviderSearchKey {
    display?: string
    searchText?: string,
}
export interface IProviderSearchInputs {
    searchText?: string,
    displayAllResults: boolean
}
export interface IProviderSearchItem {
    entity_id?: number,
    provider_name?: string,
    npi?: string,
    group_name?: string,
    rendering_provider?: string,
    active?: number,
    address1?: string,
    address2?: string,
    city?: string,
    state?: string,
    zip_code?: string,
    billing_provider_id?: number
}

export interface ICodeSearchKey {
    display?: string
    searchText?: string,
}
export interface ICodeSearchInputs {
    searchText?: string,
    displayAllResults: boolean
}
export interface ICodeSearchItem {
    codeId?: number,
    code?: string,
    codeShortDesc?: string,
    codeType?: string,
    codeStartDate?: string,
    codeAndDesc?: string
    //codeEndDate: Date
}

export interface IServiceLocationSearchKey {
    display?: string
    searchText?: string,
}

export interface IHeirItem<TItem> {
    name?: string,
    children?: Array<TItem>[]
}

export const FHIR = {
    async getApiClients(): Promise<FhirApiModel[]> {
        return await getJson("api/SmartApiInfo/Clients") as FhirApiModel[];
    },
    async getClinicians(): Promise<ClinicianModel[]> {
        return await getJson("api/SmartApiInfo/Clinicians") as ClinicianModel[];
    },
    async ehrLaunchUrl(apiId: string, clinicianId: string, patientId?: string): Promise<StringOrNothing> {
        if (patientId)
            return await getText(`api/SmartApiInfo/EhrLaunchUrl/${apiId}/${clinicianId}/${patientId}`);
        return await getText(`api/SmartApiInfo/EhrLaunchUrl/${apiId}/${clinicianId}`);
    }
}

export interface IIntakeFilterModel {
    intake_filter_id?: number,
    template_id?: number,
    filter_option?: IntakeFilterOption,
    last_encounter_time_interval?: number,
    last_encounter_time_unit?: string,
    intake_specific_date?: NoTzDate
}

export type StringOrNothing = string | undefined;

// Call the API endpoint.  If there is an error, call errorCallback (if not defined we show an alert) and return nothing
export async function getText(url: string, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    const resp = await instrumentedFetch(fullUrl(url), "GET");
    return handleResponse(resp, errorCallback);
}

// Make a GET call to a URL to fetch a resource
export async function getFromUrl(url: string, callOptions?: CallOptions): Promise<Response>
{
    const resp = await fetch(url, {
        method: "GET",
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: fetchHeaders(callOptions)
    });
    return resp;
}

// Examine the HTTP response and manage any errors accordingly
async function handleResponse(resp: Response, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    // 204 => No content
    if (204 == resp.status)
        return undefined;

    // Check if the call returned valid data
    const success = await validApiCall(resp, errorCallback);
    if (!success)
        return undefined;

    // Get the response
    const respText = await resp.text();
    if (!respText)
        return;
    return respText;
}

// Make a call - if it failed, return the error message
export async function getVoid(url: string, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    return getText(url, errorCallback);
}


// Call the API endpoint and return JSON response.  If there is an error, call errorCallback (if not defined we show an alert) and return nothing
export async function getJson(url: string, errorCallback?: ErrorCallbackFunction): Promise<object | undefined> {
    const respText = await getText(url, errorCallback);
    if (undefined === respText)
        return respText;

    return JSON.parse(respText);
}

export async function putJson(url: string, payload: any, errorCallback?: ErrorCallbackFunction): Promise<object | undefined> {
    const respText = await putText(url, payload, { errorCallback });
    if (undefined === respText)
        return respText;

    return JSON.parse(respText);
}

export async function postRazor(url: string, windowId: string, payload: any, width?: number, height?: number) {
    if (!width) {
        width = 940;
    }
    if (!height) {
        height = 500;
    }
    const result = await postText(url, payload);
    if (!result)
        return;
    let newWindow = window.open("about:blank", windowId, "popup=yes,resizable=yes,width=" + width + ",height=" + height + ",top=10,left=10");
    setTimeout(() => {
        // Needs a short pause before writing to the window.
        newWindow?.document.write(result ?? ""); 
    },200);
}


export async function putObject<TResp>(url: string, payload?: any, errorCallback?: ErrorCallbackFunction): Promise<TResp | undefined> {
    return (await putJson(url, payload, errorCallback)) as TResp|undefined;
}

// Call the API endpoint using GET and return the object.  If there is an error, call errorCallback (if not defined we show an alert) and return nothing
export async function getObject<TResp>(url: string, errorCallback?: ErrorCallbackFunction): Promise<TResp | undefined> {
    return await getJson(url, errorCallback) as TResp|undefined;
}

export async function getObjectWithApiError<TResp>(endpoint: string): Promise<IResultAndError<TResp>> {
    let apiError: IAPIErrorResult = { error: "Failed Request" };
    const result = await getJson(endpoint, 
        (httpResp, errorMessage) => {
            apiError.error = errorMessage;
            apiError.status = httpResp.status;
            apiError.statusText = httpResp.statusText;
        }) ?? apiError;

    if ("object" === typeof(result) && "error" in result)
        return { error: apiError };
    return { success: <any>result };
}

// Make a DELETE call and return any text response
export async function deleteText(url: string, payload?: any, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    const resp = await instrumentedFetch(fullUrl(url), "DELETE", JSON.stringify(payload));
    return handleResponse(resp, errorCallback);
}

// Make a DELETE call that doesn't return a response (any text we get would indicate an error)
export async function deleteVoid(url: string, payload?: any, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    return deleteText(url, payload, errorCallback);
}

// It seems like this is a OBSOLETE function
export async function deleteVoidWithBoolResult(url: string, errorCallback?: ErrorCallbackFunction): Promise<boolean> {
    let isGood = true;

    // Handle make sure to create an error handler
    async function errMessageHander(httpResp: Response, errorMessage?: string): Promise<void> {
        await validApiCall(httpResp, errorCallback);

        isGood = false;
    };

    deleteText(url, errMessageHander);
    return isGood;
}

export async function deleteJson(url: string, payload?: any, errorCallback?: ErrorCallbackFunction): Promise<any> {
    const resp = await instrumentedFetch(fullUrl(url), "DELETE", JSON.stringify(payload));

    // Check for errors
    if (!await validApiCall(resp, errorCallback))
        return;

    if (204 == resp.status)
        return; 

    return resp.json();
}
export async function deleteObject<TResp>(url: string, payload?: any, errorCallback?: ErrorCallbackFunction): Promise<TResp | undefined> {
    return await deleteJson(url, payload, errorCallback) as TResp | undefined;
}

export async function deleteObjectWithApiError<TResult>(endpoint: string, payload?: object): Promise<IResultAndError<TResult>> {
    let apiError: IAPIErrorResult = { error: "Failed Request" };
    const result = await deleteJson(endpoint, payload,
        (httpResp, errorMessage) => {
            apiError.error = errorMessage;
            apiError.status = httpResp.status;
            apiError.statusText = httpResp.statusText;            
        }) ?? apiError;

    if ("object" === typeof(result) && "error" in result)
        return { error: apiError };
    return { success: result };
}

export function fullUrl(apiUrl: string)	: string
{
	return combinePaths([baseUri, apiUrl], false);
}

export function combinePaths(args: (string|null|undefined)[], isUrl: boolean) : string
{
    if (args.length < 2) {
        assertInternally(`Expected at least 2 arguments but got ${args.length}`);
        return "";
    }

	let prefix = args.shift();
	let suffix = args[0];

    if (prefix === null || prefix === undefined || suffix === null || suffix === undefined) {
        assertInternally(`Expected 2 strings but got '${prefix}' and '${suffix}`);
        return "";
    }

    if (prefix !== "" && !prefix.endsWith("/"))
        prefix += "/";
    if (suffix !== "" && suffix.startsWith("/"))
        suffix = suffix.replace("/","");

    let path = prefix + suffix;

    if (args.length < 2) { // check for less than 2 because of args.shift above
        if (isUrl) {
            let urlPath = "";
            try {
                urlPath = new URL(suffix,prefix).href;
            } catch (error: any) {
                assertInternally(error.message);
            }
            return urlPath;
        }
        return path;
    }

    args[0] = path;

	return combinePaths(args, isUrl);
}

// Make a POST call and return any text response
export async function postText(url: string, payload: any, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    const resp = await instrumentedFetch(fullUrl(url), "POST", JSON.stringify(payload));
    return handleResponse(resp, errorCallback);
}

// Make a POST call that doesn't return a response (any text we get would indicate an error)
export async function postVoid(url: string, payload: any, errorCallback?: ErrorCallbackFunction): Promise<StringOrNothing> {
    return postText(url, payload, errorCallback);
}

export interface IVoidResult
{
    success: boolean,
    errorMsg?: string
}

// Make a POST call and return a result
export async function postVoidResult(url: string, payload: any, errorCallback?: ErrorCallbackFunction): Promise<IStatusResponse>
{
    // Create an error stub to swallow the error
    let errorMsg: string|undefined;
    if (!errorCallback)
    {
        errorCallback = function (_httpResp, errorMessage) {
            errorMsg = getErrorMessageFromErrorCallback(_httpResp, errorMessage);;
        };
    }

    await postVoid(url, payload, errorCallback);
    if (!errorMsg)
    {
        return { successful: true};
    }
    return { successful: false, msg: errorMsg};
}

// Make a PUT call and return any text response
export async function putText(url: string, payload: any, callOptions?: CallOptions): Promise<StringOrNothing> {
    let body = JSON.stringify(payload);
    if ("string" === typeof(payload))
    {
        body = payload;
    }

    const resp = await instrumentedFetch(fullUrl(url), "PUT", body, callOptions);
    return handleResponse(resp, callOptions?.errorCallback);
}

// Make a PUT call that doesn't return a response (any text we get would indicate an error)
export async function putVoid(url: string, payload?: any, callOptions?: CallOptions): Promise<StringOrNothing> {
    return putText(url, payload, callOptions);
}

export async function putVoidResult(url: string, payload?: any, callOptions?: CallOptions): Promise<IStatusResponse>
{
    let errorMsg: string|undefined;
    if (!callOptions?.errorCallback)
    {
        if (!callOptions)
            callOptions = {};

        callOptions.errorCallback = function(_httpResp, errorMessage){
            errorMsg = getErrorMessageFromErrorCallback(_httpResp, errorMessage);
        }
    }

    await putVoid(url, payload, callOptions);
    if (!errorMsg)
    {
        return { successful: true};
    }
    return { successful: false, msg: errorMsg};
}


// Checks the amount of time an API call took and passes the data off to the performance code to decide what to do.
async function instrumentedFetch(url: string, method: HttpMethods, payload?: string, callOptions?: CallOptions): Promise<Response>
{
    const startTime = new Date().getTime();
    const resp = await fetch(url, {
        method: method,
        cache: 'no-cache',
        credentials: 'same-origin',
        body: payload,
        headers: fetchHeaders(callOptions)
    });
    const endTime = new Date().getTime();

    // Keep the timer alive
    const automatedFetch = callOptions?.automatedCall ?? false;
    // Add automated fetch calls here so timer does not get reset and the window can close if there is no activity.
    if (typeof(MessageLib) != "undefined" && !automatedFetch) {
        MessageLib.MainWindow.keepAlive();
    }

    // Toss the data at the performance code for it to decide what to do with it
    addDegradedRequestDataItem({
        method: method,
        endpointPath: url,
        body: payload,
        handler: "ChoiceClientLib.ChoiceApi",
        openTimeEpoch: startTime,
        completedTimeEpoch: endTime,
        httpStatus: resp.status
    });

    return resp;
}


// Make a POST calls -- if there is an error, return 'undefined'

export interface IResultAndError<TResult> {
    success?: TResult,
    error?: IAPIErrorResult,
}

// Get the error text
export function getApiErrorText(apiError: IAPIErrorResult): string | undefined
{
    const errText = apiError.error ?? apiError.statusText;
    if (errText)
        return errText;

    if (apiError.status)
        `HTTP ${apiError.status}`;
}

export interface IAPIErrorResult {
    error?: string,
    status?: number,
    statusText?: string
}
export async function postJsonWithApiError<TResult>(endpoint: string, payload: object): Promise<TResult | IAPIErrorResult> {
    console.log("postJsonWithApiError is obsoleted; use postObjectWithApiError")
    let apiError: IAPIErrorResult = { error: "Failed Request" };
    const result = await postJson(endpoint, payload,
        (httpResp, errorMessage) => {
            apiError.error = errorMessage;
        }) ?? apiError;

    return result;
}
export async function postObjectWithApiError<TResult>(endpoint: string, payload: object): Promise<IResultAndError<TResult>> {
    let apiError: IAPIErrorResult = { error: "Failed Request" };
    const result = await postJson(endpoint, payload,
        (httpResp, errorMessage) => {
            apiError.error = errorMessage;
            apiError.status = httpResp.status;
            apiError.statusText = httpResp.statusText;            
        }) ?? apiError;

    if ("object" === typeof(result) && "error" in result)
        return { error: apiError };
    return { success: result };
}

type HttpMethods = "GET" | "HEAD" | "POST" | "PUT" | "DELETE" | "CONNECT" | "OPTIONS" | "TRACE" | "PATCH";

interface CallOptions
{
    headers?: Record<string,string>,
    errorCallback?: ErrorCallbackFunction,
    automatedCall?: boolean
}

function fetchHeaders(callOptions?: CallOptions){
    // Default to json for content type if not specified
    let headers = callOptions?.headers ?? {};
    if (!headers["Content-Type"])
    {
        headers["Content-Type"] = "application/json";
    }
    return headers;
}

async function fetchJsonAndHeader(method: HttpMethods, url: string, payload: any, callOptions?: CallOptions): Promise<any> {
    const resp = await instrumentedFetch(fullUrl(url), method, JSON.stringify(payload), callOptions);    

    // Check for errors
    if (!await validApiCall(resp, callOptions?.errorCallback))
        return;

    if (204 == resp.status)
        return;

    return resp.json();
}

export async function postJson(url: string, payload: any, errorCallback?: ErrorCallbackFunction): Promise<any> {
    return await fetchJsonAndHeader("POST", url, payload, { errorCallback: errorCallback});
}

// Call the API endpoint using POST and return the object.  If there is an error, call errorCallback (if not defined we show an alert) and return nothing
export async function postObject<TResp>(url: string, payload: any, callOptions?: CallOptions): Promise<TResp | undefined> {
    return await fetchJsonAndHeader("POST", url, payload, callOptions) as TResp;
}


enum ContentType {
    json = "application/json",
    textHTML = "text/html",
    textPlain = "text/plain",
    textXML = "text/xml",
    java = "application/java-archive",
    javaScript = "application/javascript",
    xml = "application/xml",
    zip = "application/zip",
    pdf = "application/pdf",
    edi12 = "application/EDI-X12",
    ediFACT = "application/EDIFACT",
    octet = "application/octet-stream",
    ogg = "application/ogg"
}

function getContentType(httpResp: Response): string | undefined
{
    return httpResp.headers.get("content-type") ?? undefined;
}

// See if the response has the specfied content type
function CheckContentType(httpResp: Response, content_type: ContentType) {
    const contentType = getContentType(httpResp);
    if (contentType) {
        if (contentType.split(";").filter(item => item.indexOf(content_type) != -1).length > 0)
            return true;
    }
    return false;

}
// Inspect an HTTP response.  If there is an error call errorCallback or display with an alert box.
export async function validApiCall(resp: Response, errorCallback?: ErrorCallbackFunction): Promise<boolean> {
    if (200 == resp.status || 204 == resp.status)
        return true; 
    // Get an error message to show the user
    const tempErrorMessage = await getErrorMessageFromResponse(resp);

    if (!errorCallback)
    {
        // If we get a non-JSON response, just indicate a general error
        if (!CheckContentType(resp, ContentType.json))
        {
            console.log(tempErrorMessage);
            const userMsg = userFriendlyErrorMessage(resp, tempErrorMessage) ?? "There was an error making an API call. Details in console.";
            showToast(userMsg, DisplayStyle.Danger);
            return false;
        }

        console.log(tempErrorMessage);
        const errorMsg = userFriendlyErrorMessage(resp, tempErrorMessage) ?? tempErrorMessage ?? `Unknown error (Http Status: ${resp.status}`;
        showToast(errorMsg, DisplayStyle.Danger);
    }
    else
    {
        errorCallback(resp, tempErrorMessage);
    }

    // Dump response text to the console.
    try {
        console.log(tempErrorMessage);
    }
    catch (e) {
        // Swallow error with possible details
    }
    return false;
}

function userFriendlyErrorMessage(resp: Response, tempErrorMessage?: string): string | undefined
{
    switch(resp.status)
    {
        case 404:
            return `Request endpoint not found (404) - ${resp.url}`;

        case 502: 
            return `Request endpoint not available (502) - ${resp.url}`;

        default:
            if (!tempErrorMessage) { 
                return `HTTP Error Status: ${resp.status}`;
            }
    }
}

// Try to get an error message from the HTTP response
async function getErrorMessageFromResponse(resp: Response): Promise<string|undefined>
{
    // Did we get JSON?
    if (CheckContentType(resp, ContentType.json)) {
        try {
            const respJson = await resp.json();
            if (respJson.error)
                return respJson.error;
            else if ("localhost" === location.host) {
                alert(`Error: ${JSON.stringify(respJson) ?? ""}`);
            }
        }
        catch (e) {
            console.log("Unable to read error message json");
        }
    }
    else {
        try {
            return await resp.text();
        }
        catch (e) {
            console.log("Unable to read error message text");
        }
    }    
}

// Convert the data passed to the 
function getErrorMessageFromErrorCallback(_httpResp: Response, errorMessage: string|undefined): string|undefined
{
    let errorMsg = errorMessage;
    if (!errorMsg?.length && _httpResp.status >= 400)
    {
        if (_httpResp.statusText.length)
            errorMsg = `Problem making call: ${_httpResp.statusText}.`;
        else
            errorMsg = `Problem making call: HTTP Status: ${_httpResp.status}.`;
    }
    return errorMsg;
}

// Get a bearer header
export function bearerAuthHeader(bearerToken?: string): Record<string, string>
{
    if (!bearerToken || 0 == bearerToken.trim().length)
    {
        alert("Invalid bearer token value");
        return {};
    }
    return {"Authorization": "Bearer " + bearerToken};
}
export function jsonToUrlEncoded(json: Record<string,object>) : string {
    const params = new URLSearchParams();
    for (const key in json) {
        if (json.hasOwnProperty(key)) {
            params.append(key, json[key]?.toString());
        }
    }
    return params.toString();
}

// Generic interfaces
export interface IStatusResponse {successful: boolean, msg?: string}

export interface ApiResponseSimple<TData>{
    success: boolean, 
    data?: TData, 
    message?: string
};

// External API parts
import * as access from './Api/Access'
import * as documentManager from './Api/DocumentManager'
import * as publicDocument from './Api/PublicDocument'
import { immunization } from './Api/Immunization'
import { legacy } from './Api/Legacy'
import * as user from './Api/User'
import { comments } from './Api/Comments'
import * as idMe from './Api/IdMe'
import * as company from './Api/Company'
import * as companySettings from './Api/CompanySettings'
import * as signature from './Api/Signature'
import * as prescription from './Api/Prescription'
import * as timeline from './Api/Timeline'
import { recall } from './Api/Recall'
import * as sentinel from './Api/Sentinel'
import * as soapNote from './Api/SoapNotes'
import * as emrOrderEntry from './Api/EmrOrderEntry'
import * as coverMyMeds from './Api/CoverMyMeds'
import * as userConfiguration from './Api/UserConfiguration'
import * as patient from './Api/Patient'
import * as ehiExport from './Api/EHIExport'
import * as userSession from './Api/UserSession'
import * as customerSupport from './Api/CustomerSupport'
import * as patientFlagSetup from './Api/PatientFlagSetup'
import * as patientAction from './Api/PatientAction'
import * as performance from './Api/Performance'
import * as list from './Api/List'
import * as schedule from './Api/Schedule'
import * as quickPay from './Api/QuickPay'
import * as activeReport from './Api/ActiveReport'
import * as reporting from './Api/Reporting'
import * as serviceLocation from './Api/ServiceLocation';
import { addDegradedRequestDataItem } from './Api/Performance';
import * as pdfBuilder from './Api/PdfBuilder';
import * as dataQuery from './Api/DataQuery'
import * as MML from './Api/MML'
import * as updoxReminderHistory from './Api/UpdoxReminderHistory';
import * as surescriptsDirectory from './Api/SurescriptsDirectory';
import * as surescriptsRLE from './Api/SurescriptsRecordLocator'
import * as inactiveLines from './Api/InactiveLines';
import * as fishbones from './Api/Fishbones';
import * as enhancedAccess from './Api/EnhancedAccess';
import * as search from './Api/Search'
import * as estimator from './ChoicePatientEstimatorApi';
import * as audit from './Api/Audit';
import * as billing from './Api/Billing';
import * as DSI from './Api/DSI';
import * as Claim from './Api/Claim';
import * as eDocument from './Api/EDocument'
import * as CCD from './Api/CCD'
import * as Intake from './Api/Intake'
import * as DiagnosisCodeGroup from './Api/DiagnosisCodeGroup';
import * as appointment from './Api/Appointment';
import { assertInternally } from './Environment';

export { access, documentManager, publicDocument, immunization, legacy, user, comments, idMe, company, companySettings, timeline, recall, 
    signature, prescription, sentinel, enhancedAccess, soapNote, emrOrderEntry, patient, coverMyMeds, userConfiguration, ehiExport, userSession, 
    customerSupport, patientFlagSetup, patientAction, list, schedule, reporting, serviceLocation, performance, pdfBuilder, estimator, 
    quickPay, dataQuery, activeReport, search, audit, MML, updoxReminderHistory, billing, surescriptsDirectory, surescriptsRLE,
    inactiveLines, DSI, Claim, eDocument, CCD, fishbones, Intake, DiagnosisCodeGroup, appointment }
